<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <a-button
        type="primary"
        icon="plus"
        @click="showModal"
        :disabled="availableLevelsEmpty"
        >{{ $t("etablissement.ajouterNiveau") }}</a-button
      >
      <a-button
        type="primary"
        icon="thunderbolt"
        :loading="loadingAll"
        :disabled="loadingAll || availableLevelsEmpty"
        @click="createAllLevels"
        >{{ $t("etablissement.createAllLevels") }}</a-button
      >
    </div>

    <a-alert
      :message="$t('etablissement.niveauxHint')"
      type="info"
      show-icon
      style="margin-bottom: 16px"
    />

    <a-modal
      v-model="visibleModal"
      @cancel="handleCancel"
      :title="$t('all.enterInformationBelow')"
      width="50%"
      :footer="false"
    >
      <a-form :form="form" @submit="addLevel">
        <a-form-item :label="$t('etablissement.niveau')">
          <a-select
            v-decorator="[
              'level',
              {
                rules: [
                  {
                    required: true,
                    message: $t('requis.niveau'),
                  },
                ],
              },
            ]"
            :placeholder="$t('choisir.niveauClass')"
            @change="onCreateLevelChange"
          >
            <a-select-option
              v-for="(value, name) in availableLevels"
              :key="name"
              :value="name"
              >{{ value }}</a-select-option
            >
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('etablissement.nom')">
          <a-input
            v-decorator="[
              'name',
              {
                rules: [
                  {
                    required: true,
                    message: $t('requis.nom'),
                  },
                ],
              },
            ]"
            :placeholder="$t('etablissement.nom')"
          />
        </a-form-item>
        <a-form-item :label="$t('etablissement.fraisInscri')">
          <a-input
            type="number"
            step="0.5"
            v-decorator="[
              'inscription',
              {
                rules: [
                  {
                    required: false,
                    min: 0,
                    message: $t('requis.fraisInscri'),
                  },
                ],
              },
            ]"
          />
        </a-form-item>
        <a-divider>{{ $t("etablissement.fraisMensuelle") }}</a-divider>
        <a-form-item :label="$t('etablissement.fraisMensuel')">
          <a-input
            type="number"
            step="0.5"
            button-style="solid"
            v-decorator="[
              'monthly',
              {
                rules: [
                  {
                    required: true,
                    min: 0,
                    message: $t('requis.fraisMensuel'),
                  },
                ],
              },
            ]"
          />
        </a-form-item>
        <a-divider>{{ $t("liste.fraisTranches") }}</a-divider>
        <a-form-item>
          <a-row v-for="(item, index) in tranchesList" :key="index">
            <a-col :span="3">
              {{ $t("etablissement.tranche") }} {{ item.trancheNumber }} :
            </a-col>
            <a-col :span="11">
              <a-select
                v-model="item.months"
                mode="multiple"
                style="width: 100%"
                :placeholder="$t('profil.mois')"
              >
                <a-select-option
                  v-for="i in monthRangesList"
                  :key="i"
                  :value="i"
                >
                  {{ moment(i).format("MMMM") }}
                </a-select-option>
              </a-select></a-col
            >
            <a-col :span="8">
              <a-input-number
                style="width: 100%"
                class="ml-2"
                :value="item.amount"
                v-model="item.amount"
                :min="0"
                :placeholder="$t('paymentStatus.montant')"
            /></a-col>
            <a-col :span="2">
              <a-button
                type="dashed"
                class="ml-4"
                shape="circle"
                icon="delete"
                @click="
                  () => {
                    tranchesList.splice(index, 1);
                  }
                "
              />
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="8"></a-col>
            <a-col :span="14">
              <a-button
                type="primary"
                class="mr-3 m-auto"
                @click="
                  () => {
                    tranchesList.push({
                      trancheNumber: tranchesList.length + 1,
                      amount: undefined,
                      months: [],
                    });
                  }
                "
              >
                {{ $t("etablissement.addTranche") }}
              </a-button>
            </a-col>
          </a-row>
        </a-form-item>
        <div class="form-actions mt-0">
          <a-button
            style="width: 150px"
            type="primary"
            htmlType="submit"
            :loading="loadingLevel"
            :disabled="loadingLevel"
            class="mr-3"
            >{{ $t("action.ajouter") }}</a-button
          >
          <a-button key="back" @click="handleCancel" :disabled="loadingLevel">{{
            $t("action.annuler")
          }}</a-button>
        </div>
      </a-form>
    </a-modal>

    <a-table
      :loading="tableLeading"
      :rowKey="'_id'"
      :columns="columns"
      :data-source="data"
      :pagination="true"
      :scroll="{ x: 'max-content' }"
    >
      <template slot="level" slot-scope="text">
        <div style="text-align: center">
          {{ getLevelLabel(Number(text)) }}
        </div>
      </template>
      <template slot="studentCount" slot-scope="text">
        <a-tag color="blue">{{ text || 0 }}</a-tag>
      </template>
      <template slot="name" slot-scope="text">
        {{ text }}
      </template>
      <template slot="tranches" slot-scope="text">
        <li v-for="t in text" :key="t.trancheNumber">
          {{ $t("etablissement.tranche") }} {{ t.trancheNumber }} :
          {{ t.amount }}
        </li>
      </template>
      <span slot="operation" slot-scope="text, record">
        <a-button type="primary" @click="() => edit(record)">
          <a-icon type="edit" />
          {{ $t("action.modifier") }}
        </a-button>
      </span>
      <span slot="operationSupprimer" slot-scope="text, record">
        <a-button type="danger">
          <a-popconfirm
            :title="$t('requis.supp')"
            @confirm="() => supp(record._id)"
          >
            <a>{{ $t("action.supprimer") }}</a>
          </a-popconfirm>
        </a-button>
      </span>
    </a-table>

    <!-- UPDATE DRAWER -->
    <a-drawer
      v-if="updateDrawer"
      :title="$t('action.modifier') + ' ' + selectedLevel.name"
      :width="720"
      :visible="updateDrawer"
      :body-style="{ paddingBottom: '80px' }"
      @close="onClose"
    >
      <a-form :form="updateForm" :key="formKey">
        <a-form-item :label="$t('etablissement.niveau')">
          <a-input
            :value="getLevelLabel(Number(selectedLevel.level))"
            disabled
          />
        </a-form-item>
        <a-form-item :label="$t('etablissement.nom')">
          <a-input
            v-decorator="[
              'name',
              {
                initialValue: selectedLevel.name,
                rules: [
                  {
                    required: true,
                    message: $t('requis.nom'),
                  },
                ],
              },
            ]"
            :placeholder="$t('etablissement.nom')"
          />
        </a-form-item>
        <a-form-item :label="$t('etablissement.fraisInscri')">
          <a-input
            type="number"
            step="0.5"
            v-decorator="[
              'inscription',
              {
                initialValue: selectedLevel.inscription.toString(),
                rules: [
                  {
                    required: true,
                    min: 0,
                    message: $t('requis.fraisInscri'),
                  },
                ],
              },
            ]"
          />
        </a-form-item>
        <a-divider>{{ $t("etablissement.fraisMensuelle") }}</a-divider>
        <a-form-item :label="$t('etablissement.fraisMensuel')">
          <a-input
            type="number"
            step="0.5"
            button-style="solid"
            v-decorator="[
              'monthly',
              {
                initialValue: selectedLevel.monthly.toString(),
                rules: [
                  {
                    required: true,
                    min: 0,
                    message: $t('requis.fraisMensuel'),
                  },
                ],
              },
            ]"
          />
        </a-form-item>
        <a-divider>{{ $t("liste.fraisTranches") }}</a-divider>
        <a-form-item>
          <a-row v-for="(item, index) in selectedTranches" :key="index">
            <a-col :span="3">
              {{ $t("etablissement.tranche") }} {{ item.trancheNumber }} :
            </a-col>
            <a-col :span="11">
              <a-select
                v-model="item.months"
                mode="multiple"
                style="width: 100%"
                :placeholder="$t('profil.mois')"
              >
                <a-select-option
                  v-for="i in monthRangesList"
                  :key="i"
                  :value="i"
                >
                  {{ moment(i).format("MMMM") }}
                </a-select-option>
              </a-select></a-col
            >
            <a-col :span="8">
              <a-input-number
                style="width: 100%"
                class="ml-2"
                :min="0"
                :value="item.amount"
                v-model="item.amount"
                :placeholder="$t('paymentStatus.montant')"
            /></a-col>
            <a-col :span="2">
              <a-button
                type="dashed"
                class="ml-4"
                shape="circle"
                icon="delete"
                @click="
                  () => {
                    selectedTranches.splice(index, 1);
                  }
                "
              />
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="8"></a-col>
            <a-col :span="14">
              <a-button type="primary" class="mr-3 m-auto" @click="addTranche">
                {{ $t("etablissement.addTranche") }}
              </a-button>
            </a-col>
          </a-row>
        </a-form-item>
      </a-form>
      <div
        :style="{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: '100%',
          borderTop: '1px solid #e9e9e9',
          padding: '10px 16px',
          background: '#fff',
          textAlign: 'right',
          zIndex: 1,
        }"
      >
        <a-button :style="{ marginRight: '8px' }" @click="onClose">{{
          $t("action.annuler")
        }}</a-button>
        <a-button
          type="primary"
          @click="save"
          :loading="loadingLevel"
          :disabled="loadingLevel"
          >{{ $t("action.modifier") }}</a-button
        >
      </div>
    </a-drawer>
  </div>
</template>
<script>
/* eslint-disable */
import apiClient from "@/services/axios";
import moment from "moment";
import { mapState } from "vuex";
import { getRangeMonthsSY } from "@/utilities/settings";
import { getLevels, getAllLevels } from "@/config/LevelConstants";
import { showApiError } from "@/utilities/showApiError";

export default {
  name: "levels",
  computed: {
    ...mapState(["settings", "user"]),
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    countryCode() {
      return this.activeBuilding?.countryCode;
    },
    levelSelectData() {
      return getLevels(this.$t.bind(this), this.schoolType, this.countryCode);
    },
    allLevels() {
      return getAllLevels(this.$t.bind(this), this.countryCode);
    },
    // Levels that do not yet have an affectation classroom for this year.
    availableLevels() {
      const taken = new Set((this.data || []).map((el) => String(el.level)));
      const result = {};
      Object.entries(this.levelSelectData).forEach(([key, label]) => {
        if (!taken.has(String(Number(key)))) result[key] = label;
      });
      return result;
    },
    availableLevelsEmpty() {
      return Object.keys(this.availableLevels).length === 0;
    },
    columns() {
      return [
        {
          title: this.$t("etablissement.niveau"),
          dataIndex: "level",
          key: "level",
          width: "180px",
          scopedSlots: { customRender: "level" },
          sorter: (a, b) => a.level - b.level,
          sortDirections: ["descend", "ascend"],
        },
        {
          title: this.$t("etablissement.nom"),
          dataIndex: "name",
          key: "name",
          sorter: (a, b) =>
            String(a.name || "").localeCompare(String(b.name || "")),
          sortDirections: ["descend", "ascend"],
        },
        {
          title: this.$t("etablissement.elevesAffectes"),
          dataIndex: "studentCount",
          key: "studentCount",
          width: "160px",
          scopedSlots: { customRender: "studentCount" },
          sorter: (a, b) => (a.studentCount || 0) - (b.studentCount || 0),
          sortDirections: ["descend", "ascend"],
        },
        {
          title: this.$t("etablissement.fraisInscri"),
          dataIndex: "inscription",
          key: "inscription",
          sorter: (a, b) => a.inscription - b.inscription,
          sortDirections: ["descend", "ascend"],
        },
        {
          title: this.$t("etablissement.fraisMensuel"),
          dataIndex: "monthly",
          key: "monthly",
          sorter: (a, b) => a.monthly - b.monthly,
          sortDirections: ["descend", "ascend"],
        },
        {
          title: this.$t("etablissement.tranches"),
          dataIndex: "tranches",
          key: "tranches",
          scopedSlots: { customRender: "tranches" },
        },
        {
          title: this.$t("all.edit"),
          dataIndex: "operation",
          scopedSlots: { customRender: "operation" },
          fixed: "right",
        },
        {
          title: this.$t("action.supprimer"),
          dataIndex: "operationSupprimer",
          scopedSlots: { customRender: "operationSupprimer" },
          fixed: "right",
        },
      ];
    },
  },
  beforeMount() {
    this.form = this.$form.createForm(this, { name: "levelForm" });
    this.updateForm = this.$form.createForm(this, { name: "updateLevelForm" });
  },
  async created() {
    this.schoolType = this.activeBuilding.type;
    this.monthRangesList = getRangeMonthsSY(
      this.settings.activeSchoolarYear,
      this.settings.schoolarYears
    );
    this.getLevelsList();
  },
  data() {
    return {
      moment,
      schoolType: null,
      tranchesList: [],
      monthRangesList: [],
      selectedLevel: {},
      selectedTranches: [],
      formKey: 0,
      updateDrawer: false,
      tableLeading: true,
      visibleModal: false,
      form: this.$form.createForm(this),
      updateForm: this.$form.createForm(this),
      data: [],
      loadingLevel: false,
      loadingAll: false,
    };
  },
  methods: {
    getLevelLabel(level) {
      return this.allLevels[String(level)] || "";
    },
    addTranche() {
      this.selectedTranches.push({
        trancheNumber: this.selectedTranches.length + 1,
        amount: undefined,
        months: [],
      });
    },
    onCreateLevelChange(levelKey) {
      const label =
        this.availableLevels[levelKey] ||
        this.levelSelectData[levelKey] ||
        String(levelKey);
      this.form.setFieldsValue({ name: label });
    },
    async getLevelsList() {
      this.tableLeading = true;
      try {
        const [levelsRes, statsRes] = await Promise.all([
          apiClient.post("/classrooms/filter", {
            query: {
              schoolarYear: this.settings.activeSchoolarYear,
              status: "active",
              classroomType: "affectation",
            },
            aggregation: {
              $project: { __v: 0, updatedAt: 0, teachers: 0 },
            },
          }),
          apiClient.get("/classrooms/inscription-stats"),
        ]);

        const counts = {};
        ((statsRes.data && statsRes.data.classrooms) || []).forEach((c) => {
          counts[String(c._id)] = c.count || 0;
        });

        this.data = (levelsRes.data || []).map((elem) => ({
          ...elem,
          key: elem._id,
          studentCount: counts[String(elem._id)] || 0,
        }));
      } catch (e) {
        showApiError(this, e);
      } finally {
        this.tableLeading = false;
      }
    },
    onClose() {
      this.updateDrawer = false;
    },
    showModal() {
      this.visibleModal = true;
    },
    handleCancel() {
      this.form.resetFields();
      this.tranchesList = [];
      this.visibleModal = false;
    },
    addLevel(e) {
      e.preventDefault();
      const thereEmpty = this.tranchesList.filter(
        (s) => !s.trancheNumber || !s.amount
      );
      if (thereEmpty.length > 0) {
        this.$message.warning(this.$t("etablissement.verifierTranches"));
        return;
      }
      this.form.validateFieldsAndScroll((err, values) => {
        if (err) return;
        this.loadingLevel = true;
        const payload = {
          classroomType: "affectation",
          name: values.name,
          level: Number(values.level),
          inscription: Number(values.inscription) || 0,
          monthly: Number(values.monthly) || 0,
          tranches: this.tranchesList,
        };
        apiClient
          .put("/classrooms", { data: payload })
          .then(() => {
            this.$message.success(this.$t("success.niveauAjout"));
            this.handleCancel();
            this.getLevelsList();
          })
          .catch((e) => showApiError(this, e))
          .finally(() => (this.loadingLevel = false));
      });
    },
    async createAllLevels() {
      const missing = Object.entries(this.availableLevels);
      if (missing.length === 0) return;
      this.loadingAll = true;
      let created = 0;
      for (const [key, label] of missing) {
        try {
          await apiClient.put("/classrooms", {
            data: {
              classroomType: "affectation",
              name: label,
              level: Number(key),
              inscription: 0,
              monthly: 0,
              tranches: [],
            },
          });
          created += 1;
        } catch (e) {
          // Skip levels that already exist or fail; continue with the rest.
          console.error(e);
        }
      }
      this.loadingAll = false;
      if (created > 0) this.$message.success(this.$t("success.niveauxCrees"));
      this.getLevelsList();
    },
    edit(record) {
      this.selectedLevel = { ...record };
      this.selectedTranches = this.selectedLevel.tranches
        ? this.selectedLevel.tranches.map((t) => ({ ...t }))
        : [];
      this.formKey += 1;
      this.updateDrawer = true;
    },
    save(e) {
      e.preventDefault();
      this.loadingLevel = true;
      this.updateForm.validateFieldsAndScroll((err, values) => {
        if (err) {
          this.loadingLevel = false;
          return;
        }
        const updates = {
          name: values.name,
          inscription: Number(values.inscription) || 0,
          monthly: Number(values.monthly) || 0,
          tranches: this.selectedTranches,
        };
        apiClient
          .patch("/classrooms/" + this.selectedLevel._id, { data: updates })
          .then(() => {
            this.$message.success(this.$t("success.niveauMSA"));
            this.updateDrawer = false;
            this.getLevelsList();
          })
          .catch((e) => showApiError(this, e))
          .finally(() => (this.loadingLevel = false));
      });
    },
    supp(key) {
      apiClient
        .delete("/classrooms/" + key)
        .then(() => {
          this.$message.success(this.$t("success.niveauSupp"));
          this.data = this.data.filter((item) => item.key !== key);
        })
        .catch((e) => showApiError(this, e));
    },
  },
};
</script>
<style scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
